/**
 * ALTIORA · Admin Sales Config (Sprint 1 — Founder only)
 *
 * Central console where the founder configures :
 *   - Pipeline stages (label, order, color, SLA, won/lost flags)
 *   - Product catalogue (label, commission_rate, trailing_rate)
 *   - AI blockage detection rule (stall_days, system_prompt)
 *   - Default revenue goal template (monthly / annual / currency)
 *
 * Nothing is hard-coded — this page is the source of truth for every
 * cabinet, market, or region the platform serves.
 */
import { useEffect, useState, useCallback } from "react";
import { Save, Plus, Trash2, GripVertical, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  PageShell, PageHeader, Card, Section, PrimaryButton, GhostButton, DangerButton,
  FormField, Input, Textarea, Select, Checkbox, Loader, TabsShell, Alert,
} from "@/components/altiora/PremiumUI";


export default function AdminSalesConfig() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [tab, setTab] = useState("stages");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/sales_os/config");
      setCfg(data);
    } catch (err) {
      toast.error("Chargement de la configuration impossible");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!cfg) return;
    setSaving(true);
    try {
      const { data } = await api.patch("/sales_os/config", cfg);
      setCfg(data);
      toast.success("Configuration enregistrée");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sauvegarde impossible");
    } finally { setSaving(false); }
  };

  return (
    <PageShell testid="admin-sales-config-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <PageHeader
          eyebrow="Fondateur · Configuration"
          title="Sales OS · Configuration"
          subtitle="Adaptez le pipeline, les produits et les règles IA sans toucher au code. Ces réglages s'appliquent à tous les conseillers de la plateforme."
          testid="admin-sales-config-header"
          actions={
            <>
              <GhostButton onClick={load} testid="admin-sales-refresh">
                <RefreshCw size={13} /> Recharger
              </GhostButton>
              <PrimaryButton onClick={save} disabled={saving}
                             testid="admin-sales-save">
                <Save size={13} /> {saving ? "Enregistrement…" : "Enregistrer"}
              </PrimaryButton>
            </>
          }
        />

        {loading && <div className="py-16 flex justify-center"><Loader /></div>}

        {!loading && cfg && (
          <>
            <TabsShell
              testid="admin-sales-tabs"
              tabs={[
                { id: "stages",   label: "Étapes du pipeline" },
                { id: "products", label: "Produits & commissions" },
                { id: "ai",       label: "Règles IA" },
                { id: "goals",    label: "Objectifs par défaut" },
              ]}
              active={tab}
              onChange={setTab}
            />

            {tab === "stages"   && <StagesEditor cfg={cfg} setCfg={setCfg} />}
            {tab === "products" && <ProductsEditor cfg={cfg} setCfg={setCfg} />}
            {tab === "ai"       && <AiRulesEditor cfg={cfg} setCfg={setCfg} />}
            {tab === "goals"    && <GoalsEditor cfg={cfg} setCfg={setCfg} />}
          </>
        )}
      </div>
    </PageShell>
  );
}


// ── Stages editor ─────────────────────────────────────────────────

function StagesEditor({ cfg, setCfg }) {
  const setStages = (stages) => setCfg({ ...cfg, stages });
  const addStage = () => setStages([
    ...cfg.stages,
    {
      key: `stage_${cfg.stages.length + 1}`,
      label: "Nouvelle étape",
      order: cfg.stages.length,
      color: "#94A3B8",
      is_won: false, is_lost: false, sla_days: 14,
    },
  ]);
  const updateStage = (i, patch) => setStages(
    cfg.stages.map((s, idx) => idx === i ? { ...s, ...patch } : s),
  );
  const removeStage = (i) => setStages(cfg.stages.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= cfg.stages.length) return;
    const next = [...cfg.stages];
    [next[i], next[j]] = [next[j], next[i]];
    // Rewrite orders so drag&drop matches persisted values
    next.forEach((s, idx) => { s.order = idx; });
    setStages(next);
  };

  return (
    <Card className="p-4" testid="admin-stages-card">
      <Section title="Étapes du pipeline"
               hint="Les colonnes du Kanban de vos conseillers. L'ordre est celui d'affichage.">
        <Alert variant="info" testid="admin-stages-info">
          Cochez « Vente gagnée » sur l'étape finale qui déclenche l'enregistrement automatique
          de la commission. Cochez « Perdu » sur l'étape d'abandon.
        </Alert>
        <div className="space-y-3 mt-3">
          {cfg.stages.map((s, i) => (
            <div key={i}
                 data-testid={`admin-stage-row-${i}`}
                 className="rounded-lg p-3 bg-white"
                 style={{ border: "1px solid #C5A88033" }}>
              <div className="flex items-start gap-2">
                <div className="flex flex-col gap-1 pt-2">
                  <button onClick={() => move(i, -1)} disabled={i === 0}
                          data-testid={`admin-stage-up-${i}`}
                          className="text-[10px] disabled:opacity-30"
                          aria-label="Monter">▲</button>
                  <button onClick={() => move(i, 1)} disabled={i === cfg.stages.length - 1}
                          data-testid={`admin-stage-down-${i}`}
                          className="text-[10px] disabled:opacity-30"
                          aria-label="Descendre">▼</button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-6 gap-2 flex-1">
                  <FormField label="Clé technique" testid={`admin-stage-key-${i}`}>
                    {({ id }) => (
                      <Input id={id} value={s.key}
                             onChange={(e) => updateStage(i, { key: e.target.value })}
                             testid={`admin-stage-key-input-${i}`} />
                    )}
                  </FormField>
                  <FormField label="Libellé" testid={`admin-stage-label-${i}`}>
                    {({ id }) => (
                      <Input id={id} value={s.label}
                             onChange={(e) => updateStage(i, { label: e.target.value })}
                             testid={`admin-stage-label-input-${i}`} />
                    )}
                  </FormField>
                  <FormField label="Couleur" testid={`admin-stage-color-${i}`}>
                    {({ id }) => (
                      <Input id={id} type="color" value={s.color}
                             onChange={(e) => updateStage(i, { color: e.target.value })}
                             testid={`admin-stage-color-input-${i}`} />
                    )}
                  </FormField>
                  <FormField label="SLA (jours)" testid={`admin-stage-sla-${i}`}>
                    {({ id }) => (
                      <Input id={id} type="number" min="0"
                             value={s.sla_days ?? ""}
                             onChange={(e) => updateStage(i, {
                               sla_days: e.target.value === ""
                                 ? null : parseInt(e.target.value, 10),
                             })}
                             testid={`admin-stage-sla-input-${i}`} />
                    )}
                  </FormField>
                  <div className="flex flex-col justify-center gap-1 pt-4">
                    <Checkbox label="Vente gagnée" checked={!!s.is_won}
                              onChange={(e) => updateStage(i, { is_won: e.target.checked,
                                                                   is_lost: false })}
                              testid={`admin-stage-won-${i}`} />
                    <Checkbox label="Perdu" checked={!!s.is_lost}
                              onChange={(e) => updateStage(i, { is_lost: e.target.checked,
                                                                   is_won: false })}
                              testid={`admin-stage-lost-${i}`} />
                  </div>
                  <div className="flex items-center justify-end">
                    <DangerButton onClick={() => removeStage(i)}
                                  testid={`admin-stage-remove-${i}`}>
                      <Trash2 size={12} />
                    </DangerButton>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-3">
          <GhostButton onClick={addStage} testid="admin-stage-add">
            <Plus size={13} /> Ajouter une étape
          </GhostButton>
        </div>
      </Section>
    </Card>
  );
}


// ── Products editor ────────────────────────────────────────────────

function ProductsEditor({ cfg, setCfg }) {
  const setProducts = (products) => setCfg({ ...cfg, products });
  const add = () => setProducts([
    ...cfg.products,
    { key: `product_${cfg.products.length + 1}`, label: "Nouveau produit",
      commission_rate: 0.5, trailing_rate: 0.05, unit: "$" },
  ]);
  const update = (i, patch) => setProducts(
    cfg.products.map((p, idx) => idx === i ? { ...p, ...patch } : p),
  );
  const remove = (i) => setProducts(cfg.products.filter((_, idx) => idx !== i));

  return (
    <Card className="p-4" testid="admin-products-card">
      <Section title="Produits & commissions"
               hint="Taux de commission première année et de renouvellement pour chaque produit.">
        <div className="space-y-3">
          {cfg.products.map((p, i) => (
            <div key={i} data-testid={`admin-product-row-${i}`}
                 className="rounded-lg p-3 bg-white grid grid-cols-1 sm:grid-cols-5 gap-2"
                 style={{ border: "1px solid #C5A88033" }}>
              <FormField label="Clé" testid={`admin-product-key-${i}`}>
                {({ id }) => (
                  <Input id={id} value={p.key}
                         onChange={(e) => update(i, { key: e.target.value })}
                         testid={`admin-product-key-input-${i}`} />
                )}
              </FormField>
              <FormField label="Libellé" testid={`admin-product-label-${i}`}>
                {({ id }) => (
                  <Input id={id} value={p.label}
                         onChange={(e) => update(i, { label: e.target.value })}
                         testid={`admin-product-label-input-${i}`} />
                )}
              </FormField>
              <FormField label="Commission 1ère année"
                         hint="0,55 = 55 %"
                         testid={`admin-product-com-${i}`}>
                {({ id }) => (
                  <Input id={id} type="number" step="0.01" min="0" max="1"
                         value={p.commission_rate}
                         onChange={(e) => update(i, { commission_rate: parseFloat(e.target.value) || 0 })}
                         testid={`admin-product-com-input-${i}`} />
                )}
              </FormField>
              <FormField label="Renouvellement"
                         hint="0,05 = 5 %"
                         testid={`admin-product-trail-${i}`}>
                {({ id }) => (
                  <Input id={id} type="number" step="0.01" min="0" max="1"
                         value={p.trailing_rate}
                         onChange={(e) => update(i, { trailing_rate: parseFloat(e.target.value) || 0 })}
                         testid={`admin-product-trail-input-${i}`} />
                )}
              </FormField>
              <div className="flex items-center justify-end">
                <DangerButton onClick={() => remove(i)}
                              testid={`admin-product-remove-${i}`}>
                  <Trash2 size={12} />
                </DangerButton>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-3">
          <GhostButton onClick={add} testid="admin-product-add">
            <Plus size={13} /> Ajouter un produit
          </GhostButton>
        </div>
      </Section>
    </Card>
  );
}


// ── AI rules editor ────────────────────────────────────────────────

function AiRulesEditor({ cfg, setCfg }) {
  const update = (i, patch) => setCfg({
    ...cfg,
    ai_rules: cfg.ai_rules.map((r, idx) => idx === i ? { ...r, ...patch } : r),
  });
  return (
    <Card className="p-4" testid="admin-ai-card">
      <Section title="Coach IA · Détection de blocage"
               hint="Configurez le comportement du coach Claude 4.6 qui analyse chaque opportunité.">
        {cfg.ai_rules.map((r, i) => (
          <div key={i} data-testid={`admin-ai-rule-${i}`}
               className="space-y-3 rounded-lg p-3 bg-white"
               style={{ border: "1px solid #C5A88033" }}>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <FormField label="Libellé" testid={`admin-ai-label-${i}`}>
                {({ id }) => (
                  <Input id={id} value={r.label}
                         onChange={(e) => update(i, { label: e.target.value })}
                         testid={`admin-ai-label-input-${i}`} />
                )}
              </FormField>
              <FormField label="Seuil de stagnation (jours)" testid={`admin-ai-stall-${i}`}>
                {({ id }) => (
                  <Input id={id} type="number" min="1"
                         value={r.stall_days}
                         onChange={(e) => update(i, { stall_days: parseInt(e.target.value, 10) || 1 })}
                         testid={`admin-ai-stall-input-${i}`} />
                )}
              </FormField>
              <div className="flex items-center pt-6">
                <Checkbox label="Règle activée" checked={r.enabled}
                          onChange={(e) => update(i, { enabled: e.target.checked })}
                          testid={`admin-ai-enabled-${i}`} />
              </div>
            </div>
            <FormField label="Prompt système du coach"
                       description="Modifie le ton et les priorités du diagnostic IA."
                       testid={`admin-ai-prompt-${i}`}>
              {({ id }) => (
                <Textarea id={id} rows={6} value={r.system_prompt}
                          onChange={(e) => update(i, { system_prompt: e.target.value })}
                          testid={`admin-ai-prompt-input-${i}`} />
              )}
            </FormField>
          </div>
        ))}
      </Section>
    </Card>
  );
}


// ── Goals template editor ──────────────────────────────────────────

function GoalsEditor({ cfg, setCfg }) {
  const g = cfg.goal_template || {};
  const update = (patch) => setCfg({ ...cfg, goal_template: { ...g, ...patch } });
  return (
    <Card className="p-4" testid="admin-goals-card">
      <Section title="Objectifs par défaut"
               hint="Valeurs utilisées par le tableau de bord Revenus quand un conseiller n'a pas défini d'objectif personnel.">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <FormField label="Objectif mensuel ($)" testid="admin-goal-monthly">
            {({ id }) => (
              <Input id={id} type="number" min="0" step="500"
                     value={g.monthly_target ?? 0}
                     onChange={(e) => update({ monthly_target: parseFloat(e.target.value) || 0 })}
                     testid="admin-goal-monthly-input" />
            )}
          </FormField>
          <FormField label="Objectif annuel ($)" testid="admin-goal-annual">
            {({ id }) => (
              <Input id={id} type="number" min="0" step="1000"
                     value={g.annual_target ?? 0}
                     onChange={(e) => update({ annual_target: parseFloat(e.target.value) || 0 })}
                     testid="admin-goal-annual-input" />
            )}
          </FormField>
          <FormField label="Devise" testid="admin-goal-currency">
            {({ id }) => (
              <Select id={id} value={g.currency || "CAD"}
                      onChange={(e) => update({ currency: e.target.value })}
                      testid="admin-goal-currency-select">
                <option value="CAD">CAD · Dollar canadien</option>
                <option value="USD">USD · Dollar US</option>
                <option value="EUR">EUR · Euro</option>
                <option value="XAF">XAF · Franc CFA</option>
              </Select>
            )}
          </FormField>
        </div>
      </Section>
    </Card>
  );
}
